import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import BookCard from '../components/BookCard';
import { getFavoriteCafeIds } from '../utils/favoriteCafes';
import { isReservationActive, listReservationsByBookIds } from '../utils/reservations';
import { loadBookTemplatesByIds } from '../utils/templateCache';

const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function FavoriteCafesBooksPage() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [favoriteCafes, setFavoriteCafes] = useState([]);
  const [items, setItems] = useState([]);

  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const cols =
    winW <= 540 ? 'repeat(2, minmax(0,1fr))' :
    winW <= 900 ? 'repeat(3, minmax(0,1fr))' :
                  'repeat(4, minmax(0,1fr))';

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!user?.uid) {
          setFavoriteCafes([]);
          setItems([]);
          return;
        }

        const ids = await getFavoriteCafeIds(user.uid);
        if (!ids.length) {
          setFavoriteCafes([]);
          setItems([]);
          return;
        }

        const cafes = [];
        for (const pack of chunk(ids, 10)) {
          const cafesSnap = await getDocs(
            query(collection(db, 'cafes'), where(documentId(), 'in', pack))
          );
          cafesSnap.docs.forEach((d) => {
            const x = d.data() || {};
            cafes.push({
              id: d.id,
              name: x.cafeName || 'Без назви',
              address: x.cafeAddress || '',
            });
          });
        }
        cafes.sort((a, b) => a.name.localeCompare(b.name));
        setFavoriteCafes(cafes);

        const cafeNames = cafes.map((c) => c.name).filter(Boolean);
        if (!cafeNames.length) {
          setItems([]);
          return;
        }

        const bookDocs = [];
        for (const pack of chunk(cafeNames, 10)) {
          const booksSnap = await getDocs(
            query(
              collection(db, 'books'),
              where('status', '==', 'available'),
              where('cafeName', 'in', pack)
            )
          );
          bookDocs.push(...booksSnap.docs);
        }
        if (!bookDocs.length) {
          setItems([]);
          return;
        }

        const reservations = await listReservationsByBookIds(bookDocs.map((d) => d.id));
        const reservedBookIds = new Set(
          reservations.filter((r) => isReservationActive(r)).map((r) => String(r.bookId || r.id))
        );
        const visibleBookDocs = bookDocs.filter((d) => !reservedBookIds.has(String(d.id)));
        if (!visibleBookDocs.length) {
          setItems([]);
          return;
        }

        const availableByTemplate = new Map();
        const templateIdsSet = new Set();
        for (const d of visibleBookDocs) {
          const b = d.data();
          if (!b.templateId) continue;
          availableByTemplate.set(b.templateId, (availableByTemplate.get(b.templateId) || 0) + 1);
          templateIdsSet.add(b.templateId);
        }
        const templateIds = Array.from(templateIdsSet);

        const templates = await loadBookTemplatesByIds(templateIds);

        const merged = templates
          .map((t) => ({
            ...t,
            availableInFavorites: availableByTemplate.get(t.id) || 0,
          }))
          .filter((x) => x.availableInFavorites > 0)
          .sort((a, b) => (b.borrowCount ?? 0) - (a.borrowCount ?? 0));

        setItems(merged);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  return (
    <div style={{ background: '#F6F4EF', minHeight: '100vh' }}>
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h1 style={{ margin: 0 }}>Книги у вибраних кав'ярнях</h1>
          <button
            onClick={() => navigate('/')}
            style={{
              border: '1px solid #e5e7eb',
              background: '#fff',
              borderRadius: 10,
              padding: '8px 12px',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            На головну
          </button>
        </div>

        <section style={{ marginTop: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Вибрані кав'ярні</div>
          {loading ? (
            <div style={{ color: '#6b7280' }}>Завантаження…</div>
          ) : favoriteCafes.length === 0 ? (
            <div style={{ color: '#6b7280' }}>
              Ви ще не позначили жодну кав'ярню як вибрану. Переглянути список кав'ярень можна{' '}
              <Link to="/cafes" style={{ color: '#14532d', fontWeight: 700 }}>
                тут
              </Link>
              .
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {favoriteCafes.map((c) => (
                <Link
                  key={c.id}
                  to={`/cafe/${encodeURIComponent(c.name)}`}
                  style={{
                    textDecoration: 'none',
                    color: '#0b5226',
                    border: '1px solid #d1d5db',
                    borderRadius: 999,
                    padding: '7px 10px',
                    background: '#f9fafb',
                    fontWeight: 700,
                  }}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          )}
        </section>

        <section style={{ marginTop: 16 }}>
          {loading ? (
            <div style={{ color: '#6b7280' }}>Завантаження книг…</div>
          ) : items.length === 0 ? (
            <div style={{ color: '#6b7280' }}>У вибраних кав'ярнях зараз немає доступних книг.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 16 }}>
              {items.map((b) => (
                <BookCard
                  key={b.id}
                  isbn={b.isbn}
                  title={b.title}
                  author={b.author}
                  coverUrl={b.coverUrl || b.cover || b.imageUrl || b.thumbnailUrl || ''}
                  borrowPrice={b.borrowPrice ?? b.priceBorrow ?? b.price ?? null}
                  borrowCount={b.borrowCount ?? 0}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}


